import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";

import styled from "styled-components";

import Icon from "components/atoms/Icon";
import Image from "components/atoms/Image";
import Tooltip from "components/atoms/Tooltip";

import { validateTitleTextMaxLength } from "components/validations/alert";

export const MANUSCRIPT_HEADER_HEIGHT = 56;

function hashPresenceColor(value) {
    const palette = ["#2563eb", "#db2777", "#0f8f6f", "#d97706", "#7c3aed", "#0891b2", "#dc2626", "#4f46e5"];
    const source = String(value || "0");
    let hash = 0;
    for (let index = 0; index < source.length; index += 1) {
        hash = (hash * 31 + source.charCodeAt(index)) >>> 0;
    }
    return palette[hash % palette.length];
}

export function normalizePresenceParticipant(participant) {
    if (!participant) {
        return null;
    }
    const seed = participant.space_member_id || participant.user_id || participant.name || "0";
    return {
        ...participant,
        color: participant.color || hashPresenceColor(seed),
    };
}

export function useManuscriptTitleEditor({ displayName, onInvalidTitle, onRenameError, onRenameSuccess, renameTitle }) {
    const [isTitleEditing, setIsTitleEditing] = useState(false);
    const [titleDraft, setTitleDraft] = useState("");
    const titleInputRef = useRef(null);
    const cancelTitleEditRef = useRef(false);

    useEffect(() => {
        if (!isTitleEditing) {
            setTitleDraft(displayName);
        }
    }, [isTitleEditing, displayName]);

    useEffect(() => {
        if (isTitleEditing) {
            titleInputRef.current?.focus();
            titleInputRef.current?.select();
        }
    }, [isTitleEditing]);

    const saveTitle = async (value) => {
        const trimmedName = value.trim();
        if (!trimmedName) {
            setTitleDraft(displayName);
            setIsTitleEditing(false);
            onInvalidTitle?.();
            return;
        }
        if (
            !validateTitleTextMaxLength(value, () => {
                titleInputRef.current?.focus();
            })
        ) {
            return;
        }
        if (trimmedName === displayName) {
            setTitleDraft(displayName);
            setIsTitleEditing(false);
            return;
        }

        try {
            await renameTitle?.(trimmedName);
            onRenameSuccess?.(trimmedName);
            setTitleDraft(trimmedName);
        } catch (error) {
            setTitleDraft(displayName);
            onRenameError?.(error);
        } finally {
            setIsTitleEditing(false);
        }
    };

    const handleTitleKeyDown = (event) => {
        if (event.nativeEvent?.isComposing || event.keyCode === 229) {
            return;
        }
        if (event.key === "Escape") {
            cancelTitleEditRef.current = true;
            setTitleDraft(displayName);
            setIsTitleEditing(false);
            event.currentTarget.blur();
            return;
        }
        if (event.key === "Enter") {
            event.preventDefault();
            event.currentTarget.blur();
        }
    };

    const handleTitleBlur = (event) => {
        if (cancelTitleEditRef.current) {
            cancelTitleEditRef.current = false;
            return;
        }
        saveTitle(event.target.value);
    };

    return {
        handleTitleBlur,
        handleTitleKeyDown,
        isTitleEditing,
        setIsTitleEditing,
        setTitleDraft,
        titleDraft,
        titleInputRef,
    };
}

function PresenceAvatar({ participant }) {
    const label = participant?.name || "Member";
    const fallback = String(label).trim().slice(0, 1).toUpperCase() || "M";
    const [imageFailed, setImageFailed] = useState(false);
    const imageUrl = participant?.image
        ? process.env.REACT_APP_ENV === "local"
            ? `http://${process.env.REACT_APP_API_BASE_URL}${participant.image}`
            : participant.image
        : "";

    useEffect(() => {
        setImageFailed(false);
    }, [imageUrl]);

    const hasImage = Boolean(imageUrl && !imageFailed);

    return (
        <PresenceAvatarWrap $background={participant?.color || "#3b82f6"} $hasImage={hasImage} aria-label={label}>
            {hasImage ? (
                <PresenceAvatarImg src={imageUrl} alt={label} onError={() => setImageFailed(true)} />
            ) : (
                fallback
            )}
        </PresenceAvatarWrap>
    );
}

function ManuscriptEditorPageHeader({
    activeMenuKey,
    headerMenuItems,
    headerMenuRef,
    headerMenuState,
    helpMenuPosition,
    helpMenuRef,
    isConnected,
    isTitleEditing,
    manuscriptDisplayName,
    manuscriptTitleDraft,
    onHelpMenuOpen,
    onHeaderMenuAction,
    onHeaderMenuOpen,
    onShortcutOpen,
    onTitleBlur,
    onTitleChange,
    onTitleFocus,
    onTitleKeyDown,
    presenceParticipants,
    titleInputRef,
}) {
    return (
        <>
            <Header>
                <HeaderLeft>
                    <HeaderBrand>
                        <HeaderLogoLink to="/" reloadDocument>
                            <Image name={"headerLogo"} />
                        </HeaderLogoLink>
                    </HeaderBrand>
                    <ToolbarMenu aria-label="editor menu">
                        <ToolbarMenuItem
                            type="button"
                            data-manuscript-header-menu-trigger="true"
                            aria-expanded={activeMenuKey === "file"}
                            onClick={(event) => onHeaderMenuOpen("file", event.currentTarget)}
                        >
                            파일
                        </ToolbarMenuItem>
                        <ToolbarMenuItem
                            type="button"
                            data-manuscript-header-menu-trigger="true"
                            aria-expanded={activeMenuKey === "edit"}
                            onClick={(event) => onHeaderMenuOpen("edit", event.currentTarget)}
                        >
                            편집
                        </ToolbarMenuItem>
                        <ToolbarMenuItem
                            type="button"
                            data-manuscript-header-menu-trigger="true"
                            aria-expanded={activeMenuKey === "view"}
                            onClick={(event) => onHeaderMenuOpen("view", event.currentTarget)}
                        >
                            보기
                        </ToolbarMenuItem>
                        <ToolbarMenuItem
                            type="button"
                            data-manuscript-header-menu-trigger="true"
                            onClick={(event) => onHelpMenuOpen(event.currentTarget)}
                        >
                            도움말
                        </ToolbarMenuItem>
                    </ToolbarMenu>
                </HeaderLeft>
                <HeaderCenter>
                    <HeaderTitleWrap data-value={manuscriptTitleDraft || manuscriptDisplayName}>
                        <HeaderTitleInput
                            ref={titleInputRef}
                            aria-label="원고 이름"
                            value={manuscriptTitleDraft}
                            placeholder="논문원고"
                            readOnly={!isTitleEditing}
                            $editing={isTitleEditing}
                            onFocus={onTitleFocus}
                            onChange={(event) => onTitleChange(event.target.value)}
                            onKeyDown={onTitleKeyDown}
                            onBlur={onTitleBlur}
                        />
                    </HeaderTitleWrap>
                </HeaderCenter>
                <HeaderActions>
                    <Status
                        $bg={isConnected ? "#e8faf2" : "var(--color-Base1)"}
                        $color={isConnected ? "var(--color-Key)" : "var(--color-Grey1)"}
                    >
                        {isConnected ? "실시간 연결" : "연결 끊김"}
                    </Status>
                    {presenceParticipants.length ? (
                        <PresenceGroup aria-label="현재 편집 중인 멤버">
                            {presenceParticipants.slice(0, 6).map((participant) => {
                                const label = participant?.name || "Member";
                                return (
                                    <Tooltip
                                        key={participant.space_member_id || participant.user_id || label}
                                        message={label}
                                    >
                                        <PresenceAvatar participant={participant} />
                                    </Tooltip>
                                );
                            })}
                        </PresenceGroup>
                    ) : null}
                </HeaderActions>
            </Header>
            {headerMenuState
                ? createPortal(
                      <ManuscriptHeaderDropdown
                          menuRef={headerMenuRef}
                          menuState={headerMenuState}
                          itemsByKey={headerMenuItems}
                          onAction={onHeaderMenuAction}
                      />,
                      document.body,
                  )
                : null}
            {helpMenuPosition
                ? createPortal(
                      <ManuscriptHelpDropdown
                          menuRef={helpMenuRef}
                          position={helpMenuPosition}
                          onShortcutOpen={onShortcutOpen}
                      />,
                      document.body,
                  )
                : null}
        </>
    );
}

function ManuscriptHeaderDropdown({ menuRef, menuState, itemsByKey, onAction }) {
    if (!menuState) {
        return null;
    }

    return (
        <HeaderDropdownMenu ref={menuRef} $top={menuState.top} $left={menuState.left}>
            {((itemsByKey || {})[menuState.key] || []).map((item) => (
                <HeaderDropdownItem
                    key={item.id}
                    type="button"
                    onClick={() => {
                        onAction(item.id);
                    }}
                >
                    <span>{item.label}</span>
                    {item.shortcut ? <span>{item.shortcut}</span> : null}
                </HeaderDropdownItem>
            ))}
        </HeaderDropdownMenu>
    );
}

function ManuscriptHelpDropdown({ menuRef, position, onShortcutOpen }) {
    if (!position) {
        return null;
    }

    return (
        <HelpDropdownMenu ref={menuRef} $top={position.top} $left={position.left}>
            <HelpDropdownItem type="button" onClick={onShortcutOpen}>
                <span>키보드 단축키</span>
                <Icon name={"arrowRight"} size={"11px"} />
            </HelpDropdownItem>
        </HelpDropdownMenu>
    );
}

const Header = styled.header`
    position: relative;
    z-index: 20;
    display: grid;
    grid-template-columns: minmax(320px, 1fr) minmax(220px, 420px) minmax(360px, 1fr);
    align-items: center;
    gap: 16px;
    height: ${MANUSCRIPT_HEADER_HEIGHT}px;
    padding: 0 14px;
    background: var(--color-White);
    border-bottom: 1px solid var(--color-Line);
`;

const HeaderLeft = styled.div`
    display: flex;
    align-items: center;
    gap: 14px;
    min-width: 0;
`;

const HeaderBrand = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 0 0 auto;
`;

const HeaderLogoLink = styled(Link)`
    display: inline-flex;
    align-items: center;
`;

const ToolbarMenu = styled.div`
    display: flex;
    align-items: center;
    gap: 2px;
    min-width: 0;
    overflow: hidden;
`;

const ToolbarMenuItem = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 32px;
    padding: 0 10px;
    border: 0;
    border-radius: 6px;
    background: transparent;
    color: var(--color-NormalBlack);
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;

    &:hover {
        background: var(--color-Base1);
    }
`;

const HeaderCenter = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    min-width: 0;
`;

const HeaderTitleWrap = styled.label`
    display: inline-grid;
    align-items: center;
    min-width: min(480px, 42vw);
    max-width: min(480px, 42vw);
    text-align: center;
    font-size: 16px;
    font-weight: 700;

    &::after {
        content: attr(data-value) " ";
        visibility: hidden;
        white-space: pre;
        grid-area: 1 / 1;
        font: inherit;
        min-width: min(480px, 42vw);
        max-width: min(480px, 42vw);
        overflow: hidden;
    }
`;

const HeaderTitleInput = styled.input`
    grid-area: 1 / 1;
    width: 100%;
    min-width: min(480px, 42vw);
    max-width: min(480px, 42vw);
    box-sizing: border-box;
    padding: 4px 8px;
    border: 1px solid ${(props) => (props.$editing ? "var(--color-Line)" : "transparent")};
    border-radius: 6px;
    background: ${(props) => (props.$editing ? "var(--color-White)" : "transparent")};
    outline: none;
    font-size: 16px;
    font-weight: 700;
    color: var(--color-NormalBlack);
    text-align: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    cursor: text;

    &:hover {
        background: var(--color-Base1);
    }

    &:focus {
        border-color: var(--color-Grey2);
        background: var(--color-White);
    }
`;

const HeaderActions = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    min-width: 0;
`;

const Status = styled.div`
    padding: 6px 10px;
    border-radius: 999px;
    background: ${(props) => props.$bg || "var(--color-Base1)"};
    color: ${(props) => props.$color || "var(--color-NormalBlack)"};
    font-size: 12px;
    font-weight: 700;
`;

const PresenceGroup = styled.div`
    display: inline-flex;
    align-items: center;
    justify-content: flex-end;
    gap: 6px;
    min-width: 0;
`;

const PresenceAvatarWrap = styled.div`
    position: relative;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: ${(props) => (props.$hasImage ? "transparent" : props.$background || "#3b82f6")};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    border: 2px solid ${(props) => props.$background || "#3b82f6"};
    box-shadow: 0 4px 10px rgba(15, 23, 42, 0.12);
    color: #ffffff;
    font-size: 10px;
    font-weight: 700;
`;

const PresenceAvatarImg = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
`;

const HelpDropdownMenu = styled.div`
    position: fixed;
    top: ${(props) => `${props.$top}px`};
    left: ${(props) => `${props.$left}px`};
    min-width: 148px;
    padding: 4px;
    border: 1px solid var(--color-Line);
    border-radius: 6px;
    background: var(--color-White);
    box-shadow: 0 10px 20px rgba(15, 23, 42, 0.12);
    z-index: 30;
`;

const HelpDropdownItem = styled.button`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    height: 30px;
    padding: 0 8px;
    border: 0;
    border-radius: 4px;
    background: transparent;
    color: var(--color-NormalBlack);
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;

    &:hover {
        background: var(--color-Base1);
    }
`;

const HeaderDropdownMenu = styled.div`
    position: fixed;
    top: ${(props) => `${props.$top}px`};
    left: ${(props) => `${props.$left}px`};
    min-width: 180px;
    padding: 4px;
    border: 1px solid var(--color-Line);
    border-radius: 6px;
    background: var(--color-White);
    box-shadow: 0 10px 20px rgba(15, 23, 42, 0.12);
    z-index: 30;
`;

const HeaderDropdownItem = styled.button`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    min-height: 30px;
    padding: 0 8px;
    border: 0;
    border-radius: 4px;
    background: transparent;
    color: var(--color-NormalBlack);
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;

    &:hover {
        background: var(--color-Base1);
    }
`;

export default ManuscriptEditorPageHeader;
